<template>
  <section>
    <b-col
      v-if="isLoading"
      class="px-0"
    >
      <b-skeleton
        animation="wave"
        class="mx-auto"
        width="100%"
        height="325px"
      />
      <b-skeleton
        animation="wave"
        class="mx-auto"
        width="100%"
        height="606px"
      />
      <b-skeleton
        animation="wave"
        class="mx-auto"
        width="100%"
        height="660px"
      />
      <b-skeleton
        animation="wave"
        class="mx-auto"
        width="100%"
        height="476px"
      />
    </b-col>
    <scroll-container
      v-else
      class="pt-2 mt-n30px mr-n15px"
      height-offset="125px"
    >
      <div class="pr-1">
        <b-card class="mb-2 mt-1">
          <b-form-group
            :label="$tk('ClientPortal.Admin.SubtypeName')"
          >
            <b-form-input
              v-model="nativeAd.name"
              size="sm"
              type="text"
            />
          </b-form-group>
        </b-card>
        <b-card
          class="mb-2"
          no-body
        >
          <h5 class="m-2">
            {{ nativeAd.name }} {{ $tk('Admin.NativeAd.Properties') }}
          </h5>
          <div>
            <Table
              :data="nativeAdTableProperties"
              :columns="tablePropertiesColumns"
              :is-loading="isLoading"
              type-of-table="nativeAdProperties"
              :enable-sort="false"
              :enable-pagination="false"
            />
          </div>
        </b-card>
        <b-card
          class="mb-2"
          no-body
        >
          <div class="d-flex justify-content-between align-items-center m-2">
            <h5>{{ nativeAd.name }} {{ $tk('Admin.NativeAd.Mappings') }}</h5>
            <b-button
              variant="primary"
              size="sm"
              @click="addNewMapping"
            >
              <GjIcon
                class="mr-50"
                name="Plus"
              />
              {{ $tk('Admin.NativeAd.NewMapping') }}
            </b-button>
          </div>
          <div>
            <Table
              :data="nativeAdTableMappings"
              :columns="tableMappingsColumns"
              :is-loading="isLoading"
              type-of-table="nativeAdMappings"
              :enable-sort="false"
              :enable-pagination="false"
            />
          </div>
        </b-card>
        <b-card
          class="mb-2"
        >
          <b-tabs>
            <b-tab
              active
            >
              <template #title>
                <GjIcon
                  key="desktop"
                  name="Desktop"
                />
                {{ $tk('ClientPortal.Form.AdPreview.Desktop') }}
              </template>
              <div class="mt-2 mb-1">
                {{ nativeAd.name }} - {{ $tk('ClientPortal.Form.AdPreview.DesktopPreview') }}
              </div>
              <div class="w-100 border bg-white disable-click">
                <iframe
                  :srcdoc="model.htmlContent"
                  :style="desktopIframeStyle"
                />
              </div>
            </b-tab>
            <b-tab>
              <template #title>
                <GjIcon
                  key="mobile"
                  name="Mobile"
                />
                {{ $tk('ClientPortal.Form.AdPreview.Mobile') }}
              </template>
              <b-container
                class="container-372px"
              >
                <div class="mt-2 mb-1">
                  {{ nativeAd.name }} - {{ $tk('ClientPortal.Form.AdPreview.MobilePreview') }}
                </div>
                <div class="border bg-white disable-click">
                  <iframe
                    class="m-25"
                    :srcdoc="mobileSrcdoc"
                    :style="mobileIframeStyle"
                  />
                </div>
              </b-container>
            </b-tab>
          </b-tabs>
        </b-card>
        <b-card
          class="mb-2"
        >
          <div class="mb-2">
            {{ nativeAd.name }} {{ $tk("ClientPortal.Admin.TemplateEditor") }}
          </div>
          <AceEditor
            ref="aceEditor"
            v-model="nativeAd.htmlContent"
            :value="nativeAd.htmlContent"
            lang="javascript"
            theme="github"
            width="100%"
            height="377px"
            :options="options"
            :commands="commands"
            @init="editorInit"
          />
        </b-card>
      </div>
    </scroll-container>
    <div class="border-top text-right py-1 footer-position">
      <b-button
        class="ml-50"
        size="sm"
        variant="primary"
        :disabled="saving"
        @click="saveChanges"
      >
        {{ $tk('ClientPortal.Admin.SaveChanges') }}
      </b-button>
    </div>
  </section>
</template>

<script>
import {
  computed,
  onMounted,
  ref,
  reactive,
} from '@vue/composition-api'
import store from '@/store'
import AceEditor from 'vuejs-ace-editor'
import ScrollContainer from '@/components/ScrollContainer.vue'
import translation from '@/mixins/translate'
import NativeAdsService from '@/api/admin/nativeAds.service'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import notification from '@/mixins/toastification'
import Table from '@/components/Table.vue'

export default {
  components: {
    GjIcon,
    Table,
    AceEditor,
    ScrollContainer,
  },
  mixins: [notification],
  setup(_, context) {
    const tablePropertiesColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.NativeAds.Name'),
        field: 'propertyName',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.NativeAds.DisplayName'),
        field: 'propertyDisplayName',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.NativeAds.InputTypeName'),
        field: 'inputTypeName',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.NativeAds.PreviewValue'),
        field: 'previewValue',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.NativeAds.Required'),
        field: 'required',
      },
    ]
    const tableMappingsColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.NativeAds.PropertyName'),
        field: 'propertyName',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.NativeAds.CatalogType'),
        field: 'catalogType',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.NativeAds.CatalogColumnName'),
        field: 'catalogColumnName',
      },
    ]
    const nativeAd = computed(() => store.getters.getNativeAd)
    const nativeAdTableProperties = computed(() => store.getters.getNativeAdTableProperties)
    const nativeAdTableMappings = computed(() => store.getters.getNativeAdTableMappings)
    const isLoading = ref(false)
    const options = ref({
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      fontSize: 14,
      highlightActiveLine: true,
      enableSnippets: true,
      showLineNumbers: true,
      tabSize: 2,
      showPrintMargin: false,
      showGutter: true,
    })
    const commands = ref([
      {
        name: 'save',
        bindKey: { win: 'Ctrl-s', mac: 'Command-s' },
        readOnly: true,
      },
    ])
    const model = reactive({
      htmlContent: null,
    })
    const { id } = context.root.$route.params
    const saving = ref(false)

    const updatePreview = () => {
      let value = nativeAd.value.htmlContent
      nativeAd.value.properties.forEach(
        property => {
          const name = property.propertyName
          const { previewValue } = property
          value = value.replaceAll(`{{${name}}}`, (previewValue || ''))
        },
      )
      model.htmlContent = value
    }
    const editorInit = editor => {
      // eslint-disable-next-line global-require
      require('brace/ext/language_tools') // language extension prerequsite...
      // eslint-disable-next-line global-require
      require('brace/mode/markdown')
      // eslint-disable-next-line global-require
      require('brace/mode/javascript') // language
      // eslint-disable-next-line global-require
      require('brace/mode/less')
      // eslint-disable-next-line global-require
      require('brace/theme/github')
      // eslint-disable-next-line global-require
      require('brace/snippets/javascript') // snippet
      editor.session.on('change', () => {
        updatePreview()
      })
    }

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getNativeAd', id)
      isLoading.value = false
    })

    const addNewMapping = () => {
      const [firstProperty] = nativeAd.value.properties
      nativeAd.value.columnMappings.push({
        id: 0,
        subtypeId: nativeAd.value.id,
        propertyId: firstProperty.propertyId,
        catalogType: 1,
        columnName: '',
      })
    }

    const desktopIframeStyle = computed(() => `border: 0px; width: 100%; height:${nativeAd.value.height === 0 ? '100%' : nativeAd.value.height}px; margin: 8px`)
    const mobileIframeStyle = computed(() => `border: 0px; width:${nativeAd.value.mobileWidth === 0 ? '100%' : nativeAd.value.mobileWidth}px; height:${nativeAd.value.mobileHeight === 0 ? '100%' : nativeAd.value.mobileHeight}px; margin: 8px`)
    const mobileSrcdoc = computed(() => `<div class="mobile"> ${model.htmlContent} </div>`)

    const saveChanges = async () => {
      saving.value = true
      try {
        const response = await NativeAdsService.updateNativeAd(id, nativeAd.value)
        if (response) {
          notification.methods.success(translation.methods.$tk('ClientPortal.Admin.ChangesHaveBeenSavedSuccesfully'))
        } else {
          notification.methods.error(translation.methods.$tk('ClientPortal.Admin.ErrorSavingChanges'))
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        saving.value = false
      }
    }

    return {
      nativeAd,
      options,
      commands,
      isLoading,
      editorInit,
      updatePreview,
      model,
      saveChanges,
      saving,
      desktopIframeStyle,
      mobileIframeStyle,
      nativeAdTableProperties,
      nativeAdTableMappings,
      addNewMapping,
      mobileSrcdoc,
      tableMappingsColumns,
      tablePropertiesColumns,
    }
  },
  beforeRouteLeave(to, from, next) {
    document.body.removeAttribute('style')
    next()
  },
}
</script>

<style lang="scss" scoped>

.disable-click{
  pointer-events: none
}
.footer-position {
  z-index: 10;
  position: relative;
  background-color: #f6f7f9;
}
</style>
